/**
 * Letterhead details for patient-facing printouts (the visit summary) — the best of what this app
 * has, as there is no single facility-wide branding record:
 *  * name    — REACT_APP_HOSPITAL_NAME (getHospitalName, the per-deployment name every printout
 *              uses), else the staff member's facility name;
 *  * logo    — REACT_APP_LAB_RESULT_LOGO_URL, the per-client logo CI publishes at /branding/logo.png
 *              (lab/imaging reports use the same one);
 *  * address — the staff member's facility `location.address` (from GET /profile, which includes
 *              the facility for every staff role — no facility permission needed);
 *  * phone   — REACT_APP_HOSPITAL_PHONE (optional; facilities have no phone field).
 * Imported by relative path (jest can't resolve the @util barrel reliably).
 */
import { getHospitalName } from './hospitalName'

export type ClinicBranding = {
  name: string
  address: string
  phone: string
  logoUrl?: string
}

type AddressLike = Partial<Record<'line1' | 'line2' | 'city' | 'state' | 'postal_code' | 'country', string>>

export const formatFacilityAddress = (location?: { address?: AddressLike } | null): string => {
  const address = location?.address
  if (!address) return ''
  return [address.line1, address.line2, address.city, address.state]
    .map(part => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(', ')
}

export const getClinicPhone = (): string => process.env.REACT_APP_HOSPITAL_PHONE?.trim() || ''

export const getClinicLogoUrl = (): string | undefined => process.env.REACT_APP_LAB_RESULT_LOGO_URL?.trim() || undefined

type IncludedLike = { type?: string; attributes?: unknown }
type FacilityLike = { name?: string; location?: { address?: AddressLike } | null }

/** Branding for a signed-in staff member, from their GET /profile `included` records. */
export const clinicBrandingFromProfile = (included?: IncludedLike[] | null): ClinicBranding => {
  const facility = included?.find(record => record?.type === 'facility')?.attributes as FacilityLike | undefined
  return {
    address: formatFacilityAddress(facility?.location),
    logoUrl: getClinicLogoUrl(),
    name: getHospitalName() || facility?.name?.trim() || '',
    phone: getClinicPhone()
  }
}

/** Branding without a staff profile (the patient portal): no facility address available. */
export const clinicBrandingFromEnv = (): ClinicBranding => clinicBrandingFromProfile(undefined)
